import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2 } from "lucide-react";
import { AppHeader } from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { api, MUSIKSTILE, AGB_URL, DATENSCHUTZ_URL } from "@/lib/api";
import { trackEvent } from "@/lib/metaPixel";
import { useAuth } from "@/context/AuthContext";

const empty = {
  name_verstorbener: "",
  alter_verstorbener: "",
  geschichte_erinnerungen: "",
  sprache: "Deutsch",
  musikstil: "",
};

const Field = ({ label, children, error }) => (
  <div className="space-y-2">
    <Label>{label}</Label>
    {children}
    {error && <p className="text-sm text-destructive">{error}</p>}
  </div>
);

export default function OrderFlow() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState("form"); // form | summary | done
  const [form, setForm] = useState(empty);
  const [agb, setAgb] = useState(false);
  const [datenschutz, setDatenschutz] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.name_verstorbener.trim()) e.name_verstorbener = "Bitte geben Sie den Namen an.";
    if (!String(form.alter_verstorbener).trim()) e.alter_verstorbener = "Bitte geben Sie das Alter an.";
    else if (!/^\d+$/.test(String(form.alter_verstorbener))) e.alter_verstorbener = "Bitte nur Zahlen eingeben.";
    if (!form.geschichte_erinnerungen.trim()) e.geschichte_erinnerungen = "Bitte beschreiben Sie den Verstorbenen.";
    if (!form.sprache) e.sprache = "Bitte wählen Sie eine Sprache.";
    if (!form.musikstil) e.musikstil = "Bitte wählen Sie einen Musikstil.";
    if (!agb) e.agb = "Bitte akzeptieren Sie die AGB.";
    if (!datenschutz) e.datenschutz = "Bitte akzeptieren Sie die Datenschutzerklärung.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const goSummary = () => {
    if (validate()) {
      setStep("summary");
      window.scrollTo(0, 0);
    }
  };

  const submit = async () => {
    setSubmitError("");
    setLoading(true);
    try {
      await api.post("/orders", {
        name_verstorbener: form.name_verstorbener,
        alter_verstorbener: parseInt(form.alter_verstorbener, 10),
        geschichte_erinnerungen: form.geschichte_erinnerungen,
        sprache: form.sprache,
        musikstil: form.musikstil,
        agb_akzeptiert: true,
        datenschutz_akzeptiert: true,
      });
      setStep("done");
      trackEvent("Purchase", { currency: "CHF", value: 99 });
      window.scrollTo(0, 0);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg =
        typeof detail === "string" && detail && !/<\/?[a-z!][^>]*>/i.test(detail)
          ? detail
          : "Der Auftrag konnte leider nicht übermittelt werden. Bitte versuchen Sie es erneut oder kontaktieren Sie Canto Dei.";
      setSubmitError(msg);
    } finally {
      setLoading(false);
    }
  };

  const summaryRows = [
    ["Firmenname", user?.firmenname],
    ["Ansprechpartner", user?.ansprechpartner],
    ["E-Mail", user?.email],
    ["Telefon", user?.telefon],
    ["Adresse", user?.adresse],
    ["Name des Verstorbenen", form.name_verstorbener],
    ["Alter des Verstorbenen", form.alter_verstorbener],
    ["Persönliche Geschichte und Erinnerungen", form.geschichte_erinnerungen],
    ["Sprache", form.sprache],
    ["Musikstil", form.musikstil],
  ];

  return (
    <div className="min-h-screen bg-cream flex flex-col" data-testid="order-flow-page">
      <AppHeader />
      <main className="flex-1 w-full max-w-2xl mx-auto px-6 py-16">
        {step === "form" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
            <h1 className="font-serif text-3xl sm:text-4xl font-light text-charcoal">
              Individuellen personalisierten Trauersong erstellen
            </h1>
            <p className="mt-4 text-sm text-charcoal/60 leading-relaxed">
              Ihre Kundendaten werden automatisch übernommen. Bitte ergänzen Sie die Angaben zum Trauersong.
            </p>

            <div className="mt-10 space-y-7">
              <Field label="Name des Verstorbenen" error={errors.name_verstorbener}>
                <Input
                  value={form.name_verstorbener}
                  onChange={(e) => set("name_verstorbener", e.target.value)}
                  data-testid="order-name-input"
                />
              </Field>

              <Field label="Alter des Verstorbenen" error={errors.alter_verstorbener}>
                <Input
                  type="number"
                  inputMode="numeric"
                  min="0"
                  value={form.alter_verstorbener}
                  onChange={(e) => set("alter_verstorbener", e.target.value)}
                  data-testid="order-age-input"
                />
              </Field>

              <Field label="Persönliche Geschichte und Erinnerungen" error={errors.geschichte_erinnerungen}>
                <Textarea
                  rows={7}
                  maxLength={4000}
                  value={form.geschichte_erinnerungen}
                  onChange={(e) => set("geschichte_erinnerungen", e.target.value.slice(0, 4000))}
                  data-testid="order-story-input"
                />
                <div className="flex justify-end">
                  <span className="text-xs text-charcoal/50" data-testid="order-story-counter">
                    {form.geschichte_erinnerungen.length} / 4000 Zeichen
                  </span>
                </div>
                <p className="text-xs text-charcoal/50 leading-relaxed">
                  Beschreiben Sie den Verstorbenen möglichst persönlich. Erinnerungen,
                  Eigenschaften, Familie, Beruf, Hobbys oder besondere Momente helfen uns
                  dabei, einen einzigartigen individuellen personalisierten Trauersong zu erstellen.
                </p>
              </Field>

              <Field label="Sprache" error={errors.sprache}>
                <Select value={form.sprache} onValueChange={(v) => set("sprache", v)}>
                  <SelectTrigger data-testid="order-language-select">
                    <SelectValue placeholder="Sprache wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Deutsch">Deutsch</SelectItem>
                  </SelectContent>
                </Select>
              </Field>

              <Field label="Musikstil" error={errors.musikstil}>
                <Select value={form.musikstil} onValueChange={(v) => set("musikstil", v)}>
                  <SelectTrigger data-testid="order-style-select">
                    <SelectValue placeholder="Musikstil wählen" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {MUSIKSTILE.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <div className="space-y-4 pt-2">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="agb"
                    checked={agb}
                    onCheckedChange={(v) => setAgb(!!v)}
                    className="mt-1"
                    data-testid="order-agb-checkbox"
                  />
                  <label htmlFor="agb" className="text-sm text-charcoal/80 leading-relaxed">
                    Ich akzeptiere die{" "}
                    <a href={AGB_URL} target="_blank" rel="noopener noreferrer" className="text-gold underline">
                      AGB
                    </a>
                    .
                  </label>
                </div>
                {errors.agb && <p className="text-sm text-destructive">{errors.agb}</p>}

                <div className="flex items-start gap-3">
                  <Checkbox
                    id="datenschutz"
                    checked={datenschutz}
                    onCheckedChange={(v) => setDatenschutz(!!v)}
                    className="mt-1"
                    data-testid="order-datenschutz-checkbox"
                  />
                  <label htmlFor="datenschutz" className="text-sm text-charcoal/80 leading-relaxed">
                    Ich akzeptiere die{" "}
                    <a href={DATENSCHUTZ_URL} target="_blank" rel="noopener noreferrer" className="text-gold underline">
                      Datenschutzerklärung
                    </a>
                    .
                  </label>
                </div>
                {errors.datenschutz && <p className="text-sm text-destructive">{errors.datenschutz}</p>}
              </div>

              <Button
                onClick={goSummary}
                className="w-full bg-gold hover:bg-golddark text-white rounded-sm py-6 transition-colors duration-500"
                data-testid="order-continue-button"
              >
                Weiter zur Zusammenfassung
              </Button>
            </div>
          </motion.div>
        )}

        {step === "summary" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }} data-testid="order-summary">
            <h1 className="font-serif text-3xl sm:text-4xl font-light text-charcoal">Zusammenfassung</h1>
            <p className="mt-4 text-sm text-charcoal/60">Bitte überprüfen Sie Ihre Angaben vor dem Absenden.</p>

            {submitError && (
              <p className="mt-6 text-sm text-destructive bg-destructive/5 border border-destructive/20 rounded-sm px-4 py-3" data-testid="order-submit-error">
                {submitError}
              </p>
            )}

            <div className="mt-8 bg-white border border-sand rounded-sm divide-y divide-sand">
              {summaryRows.map(([label, value]) => (
                <div key={label} className="px-6 py-4 grid sm:grid-cols-3 gap-1">
                  <span className="text-sm text-charcoal/50">{label}</span>
                  <span className="sm:col-span-2 text-sm text-charcoal whitespace-pre-line">{value || "—"}</span>
                </div>
              ))}
            </div>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Button
                variant="outline"
                onClick={() => setStep("form")}
                disabled={loading}
                className="sm:w-40 border-gold text-gold hover:bg-gold hover:text-white rounded-sm py-6 transition-colors duration-500"
                data-testid="order-back-button"
              >
                Zurück
              </Button>
              <Button
                onClick={submit}
                disabled={loading}
                className="flex-1 bg-gold hover:bg-golddark text-white rounded-sm py-6 transition-colors duration-500 disabled:opacity-100"
                data-testid="order-submit-button"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />
                    Wird übermittelt…
                  </span>
                ) : (
                  "Auftrag verbindlich absenden"
                )}
              </Button>
            </div>

            {loading && (
              <div className="mt-6 flex items-start gap-3 bg-muted border border-sand rounded-sm px-5 py-4" data-testid="order-loading-notice">
                <Loader2 className="w-5 h-5 text-gold animate-spin shrink-0 mt-0.5" strokeWidth={1.75} />
                <p className="text-sm text-charcoal/75 leading-relaxed">
                  Ihr individueller personalisierter Trauersong wird vorbereitet.
                  Bitte warten Sie einen Moment.
                </p>
              </div>
            )}
          </motion.div>
        )}

        {step === "done" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="text-center py-12"
            data-testid="order-confirmation"
          >
            <span className="inline-flex w-16 h-16 rounded-full border border-sand items-center justify-center bg-white mb-8">
              <CheckCircle2 className="w-8 h-8 text-gold" strokeWidth={1.5} />
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl font-light text-charcoal">Vielen Dank.</h1>
            <div className="mt-8 space-y-4 text-charcoal/70 leading-relaxed max-w-lg mx-auto">
              <p>Ihr Auftrag wurde erfolgreich übermittelt.</p>
              <p>Mit der Erstellung Ihres individuellen personalisierten Trauersongs wurde begonnen.</p>
              <p>Der fertige Song wird Ihnen in der Regel innerhalb von 24 Stunden per E-Mail zugestellt.</p>
            </div>
            <Button
              onClick={() => navigate("/dashboard")}
              className="mt-12 bg-gold hover:bg-golddark text-white rounded-sm px-10 py-6 transition-colors duration-500"
              data-testid="order-done-button"
            >
              Zurück zum Dashboard
            </Button>
          </motion.div>
        )}
      </main>
    </div>
  );
}
